import React, { useState, useEffect, useCallback } from 'react';
import { getBackendUrl } from '../utils/api';
import { Users, Radio, Plus, Trash2, RefreshCw, Search, Link2, AlertCircle } from 'lucide-react';

const BACKEND_URL = getBackendUrl();

const AdminCompareStationsAccess = () => {
  const [users, setUsers] = useState([]);
  const [stations, setStations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [stationA, setStationA] = useState('');
  const [stationB, setStationB] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [stats, setStats] = useState({ total: 0, withAccess: 0 });

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [usersRes, stationsRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/admin/compare-stations-access/users`),
        fetch(`${BACKEND_URL}/api/admin/compare-stations-access/stations`)
      ]);
      
      const usersData = await usersRes.json();
      const stationsData = await stationsRes.json();
      
      setUsers(usersData.users || []);
      setStations(stationsData.stations || []);
      setStats({
        total: usersData.total_users || 0,
        withAccess: usersData.users_with_access || 0
      });
    } catch (err) {
      setError('Σφάλμα φόρτωσης: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const assignPair = async () => {
    if (!selectedUser || !stationA || !stationB) {
      setError('Επιλέξτε χρήστη και δύο σταθμούς');
      return;
    }
    
    if (stationA === stationB) {
      setError('Επιλέξτε διαφορετικούς σταθμούς');
      return;
    }
    
    setAssigning(true);
    setError(null);
    
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/admin/compare-stations-access/users/${selectedUser.user_id}/pairs`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            station_a_id: stationA,
            station_b_id: stationB
          })
        }
      );
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Assignment failed');
      }
      
      const data = await response.json();
      setSuccess(data.message);
      setStationA('');
      setStationB('');
      await fetchData();
    } catch (err) {
      setError(err.message);
    } finally {
      setAssigning(false);
    }
  };

  const removePair = async (userId, pairId) => {
    if (!window.confirm('Αφαίρεση αυτού του ζευγαριού σταθμών;')) return;
    
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/admin/compare-stations-access/users/${userId}/pairs/${pairId}`,
        { method: 'DELETE' }
      );
      
      if (!response.ok) throw new Error('Delete failed');
      
      setSuccess('Το ζευγάρι αφαιρέθηκε');
      await fetchData();
    } catch (err) {
      setError(err.message);
    }
  };

  const filteredUsers = users.filter(u => 
    u.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getStationName = (id) => {
    const station = stations.find(s => s.id === id);
    return station ? station.name : 'Unknown';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-purple-500/20 rounded-lg">
            <Link2 className="w-6 h-6 text-purple-400" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-white">Compare Stations Access</h2>
            <p className="text-gray-400 text-sm">
              Διαχείριση πρόσβασης χρηστών ({stats.withAccess} με πρόσβαση / {stats.total} συνολικά)
            </p>
          </div>
        </div>
        <button
          onClick={fetchData}
          disabled={loading}
          className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg flex items-center gap-2"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Messages */}
      {error && (
        <div className="bg-red-900/50 border border-red-500 text-red-200 px-4 py-3 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          {error}
        </div>
      )}
      {success && (
        <div className="bg-green-900/50 border border-green-500 text-green-200 px-4 py-3 rounded-lg">
          {success}
        </div>
      )}

      {/* Assignment Form */}
      <div className="bg-gray-800 rounded-lg p-5 border border-gray-700">
        <h3 className="text-lg font-medium text-white mb-4">Ανάθεση Ζευγαριού Σταθμών</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* User Selection */}
          <div>
            <label className="block text-sm text-gray-400 mb-1">Χρήστης</label>
            <select
              value={selectedUser?.user_id || ''}
              onChange={(e) => {
                const user = users.find(u => u.user_id === e.target.value);
                setSelectedUser(user || null);
              }}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
            >
              <option value="">Επιλέξτε χρήστη...</option>
              {users.map(u => (
                <option key={u.user_id} value={u.user_id}>
                  {u.username} ({u.pairs_count} ζευγάρια)
                </option>
              ))}
            </select>
          </div>

          {/* Station A */}
          <div>
            <label className="block text-sm text-gray-400 mb-1">Σταθμός A</label>
            <select
              value={stationA}
              onChange={(e) => setStationA(e.target.value)}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
            >
              <option value="">Επιλέξτε σταθμό...</option>
              {stations.map(s => (
                <option key={s.id} value={s.id} disabled={s.id === stationB}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>

          {/* Station B */}
          <div>
            <label className="block text-sm text-gray-400 mb-1">Σταθμός B</label>
            <select
              value={stationB}
              onChange={(e) => setStationB(e.target.value)}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
            >
              <option value="">Επιλέξτε σταθμό...</option>
              {stations.map(s => (
                <option key={s.id} value={s.id} disabled={s.id === stationA}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>

          {/* Assign Button */}
          <div className="flex items-end">
            <button
              onClick={assignPair}
              disabled={assigning || !selectedUser || !stationA || !stationB}
              className="w-full px-4 py-2 bg-purple-600 hover:bg-purple-500 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg flex items-center justify-center gap-2"
            >
              {assigning ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <Plus className="w-4 h-4" />
              )}
              Ανάθεση
            </button>
          </div>
        </div>
      </div>

      {/* Available Stations */}
      <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
        <h4 className="text-sm font-medium text-gray-400 mb-3">
          Διαθέσιμοι Σταθμοί ({stations.length})
        </h4>
        <div className="flex flex-wrap gap-2">
          {stations.slice(0, 50).map(s => (
            <span
              key={s.id}
              className="px-3 py-1 bg-gray-700 text-gray-300 text-sm rounded-full"
            >
              {s.name}
            </span>
          ))}
          {stations.length > 50 && (
            <span className="px-3 py-1 bg-gray-600 text-gray-400 text-sm rounded-full">
              +{stations.length - 50} more
            </span>
          )}
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
        <input
          type="text"
          placeholder="Αναζήτηση χρήστη..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500"
        />
      </div>

      {/* Users List */}
      {loading ? (
        <div className="text-center py-12 text-gray-400">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-2" />
          Φόρτωση...
        </div>
      ) : (
        <div className="space-y-3">
          {filteredUsers.map(user => (
            <div
              key={user.user_id}
              className="bg-gray-800 rounded-lg p-4 border border-gray-700"
            >
              {/* User Header */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gray-700 rounded-full flex items-center justify-center">
                    <Users className="w-5 h-5 text-gray-400" />
                  </div>
                  <div>
                    <div className="font-medium text-white">{user.username}</div>
                    <div className="text-sm text-gray-400">{user.email}</div>
                  </div>
                </div>
                <div className="text-sm text-gray-400">
                  {user.package && (
                    <span className="px-2 py-1 bg-purple-500/20 text-purple-300 rounded-full text-xs">
                      {user.package}
                    </span>
                  )}
                </div>
              </div>

              {/* Assigned Pairs */}
              {user.pairs_count > 0 ? (
                <div className="space-y-2">
                  {user.pairs.map(pair => (
                    <div
                      key={pair.pair_id}
                      className="flex items-center justify-between bg-gray-700/50 rounded-lg px-4 py-2"
                    >
                      <div className="flex items-center gap-3">
                        <Radio className="w-4 h-4 text-purple-400" />
                        <span className="text-white font-medium">{pair.station_a_name}</span>
                        <span className="text-gray-500">vs</span>
                        <span className="text-white font-medium">{pair.station_b_name}</span>
                      </div>
                      <button
                        onClick={() => removePair(user.user_id, pair.pair_id)}
                        className="p-1.5 text-red-400 hover:bg-red-500/20 rounded-lg transition-colors"
                        title="Αφαίρεση"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-yellow-400/80 bg-yellow-500/10 rounded-lg px-4 py-2">
                  Χωρίς ζευγάρι σταθμών
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminCompareStationsAccess;
